/**
 * @fileoverview Shared form step tabs for renderer and conductor shells.
 * @module inspection-renderer/shell
 *
 * Responsibilities:
 * - Render the visible form summaries and select an active form.
 *
 * Dependencies:
 * - React.
 *
 * Architecture:
 * - Presentation component shared by InspectionRenderer and its conductor shell.
 *
 * Notes:
 * - Issue counts are provided by the canonical renderer.
 *
 * @component FormStepTabs
 */
import type { ReactNode } from 'react';

/**
 * @contract InspectionVisibleFormSummary
 * Purpose: Public display data for a visible inspection form.
 * Fields: id, name, issueCount, complete.
 */
export interface InspectionVisibleFormSummary {
  id: string;
  name: string;
  issueCount: number;
  complete: boolean;
}

/**
 * @contract FormStepTabsProps
 * Purpose: Props for the shared form step selector.
 * Fields: forms, activeFormId, onActiveFormIdChange, showIssueCounts.
 */
export interface FormStepTabsProps {
  forms: readonly InspectionVisibleFormSummary[];
  activeFormId: string;
  onActiveFormIdChange: (id: string) => void;
  showIssueCounts?: boolean;
}

/**
 * @component FormStepTabs
 * Purpose: Select a form step and show its blocking-issue count.
 * Props: FormStepTabsProps.
 */
export function FormStepTabs({
  forms,
  activeFormId,
  onActiveFormIdChange,
  showIssueCounts = true,
}: FormStepTabsProps): ReactNode {
  return (
    <nav
      role="tablist"
      aria-label="Inspection forms"
      data-form-tabs
      onWheel={(event) => {
        if (event.deltaY !== 0) event.currentTarget.scrollLeft += event.deltaY;
      }}
      className="flex overflow-x-auto scrollbar-none scroll-smooth"
    >
      {forms.map((form) => {
        const isActive = form.id === activeFormId;
        return (
          <button
            key={form.id}
            type="button"
            role="tab"
            data-form-id={form.id}
            data-form-issue-count={form.issueCount}
            data-form-complete={form.complete}
            aria-selected={isActive}
            onClick={() => onActiveFormIdChange(form.id)}
            className={`shrink-0 flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 transition-all whitespace-nowrap ${isActive ? 'border-orange-500 text-orange-700 bg-orange-50' : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300 hover:bg-gray-50'}`}
          >
            <span>{form.name}</span>
            {form.complete && (
              <span
                data-form-complete-check
                aria-label="Complete"
                className={`inline-flex h-4 w-4 items-center justify-center rounded-full text-[10px] font-bold ${isActive ? 'bg-white text-green-700' : 'bg-green-100 text-green-700'}`}
              >
                ✓
              </span>
            )}
            {showIssueCounts && form.issueCount > 0 && (
              <span
                data-tab-issue-badge="true"
                aria-label={`${form.issueCount} issue${form.issueCount === 1 ? '' : 's'}`}
                className={`inline-flex items-center justify-center min-w-4 h-4 px-1 text-[10px] font-semibold rounded-full ${isActive ? 'bg-white text-orange-700' : 'bg-amber-500 text-white'}`}
              >
                {form.issueCount}
              </span>
            )}
          </button>
        );
      })}
    </nav>
  );
}
